import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICreateZipCode, IZipCode, PalletisingService } from '@shared/services/palletising.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { ZipCodeToFromValidator } from '@shared/validators/ToAndFromZipCodeValicator';

@Component({
	selector: 'app-create-update-grouping-dialog',
	templateUrl: './create-update-zipcode.html',
	styleUrls: ['./create-update-zipcode.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateZipCodeDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	public form: FormGroup;
	public httpError: string;
	public loading: boolean;
	public zipCode: IZipCode | ICreateZipCode;
	public isCreatingZipCode: boolean;

	public zipCodePattern = ValidatorPatterns.ZipCodeCharacters;
	public descriptionPattern = ValidatorPatterns.DanishCharacters;

	public constructor(
		@Inject(MAT_DIALOG_DATA) public editZipCode: IZipCode | null,
		public dialogRef: MatDialogRef<CreateUpdateZipCodeDialogComponent>,
		private palletisingService: PalletisingService,
		private fb: FormBuilder,
	) {
		this.httpError = '';
		this.loading = false;
		this.zipCode = editZipCode ? { ...editZipCode } : this.defaultGrouping;
		this.isCreatingZipCode = editZipCode === null;
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	private initializeForm(): void {
		this.form = this.fb.group(
			{
				ruleNumber: [this.zipCode.ruleNumber, [Validators.required, Validators.min(1), Validators.max(1000000)]],
				from: [this.zipCode.from, [Validators.required]],
				to: [this.zipCode.to],
				description: [this.zipCode.description, [Validators.pattern(ValidatorPatterns.DanishCharacters)]],
			},
			{
				validators: [ZipCodeToFromValidator('from', 'to')],
			},
		);

		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value: IZipCode | ICreateZipCode) => {
			this.zipCode.ruleNumber = value.ruleNumber ? value.ruleNumber : null;
			this.zipCode.from = value.from ? value.from : null;
			this.zipCode.to = value.to ? value.to : null;
			this.zipCode.description = value.description ? value.description : null;
		});
	}

	public get controls() {
		return this.form.controls;
	}

	private get defaultGrouping(): ICreateZipCode {
		return {
			ruleNumber: null,
			from: null,
			to: null,
		};
	}

	public onCancel(): void {
		this.dialogRef.close();
	}

	public async onSave(): Promise<void> {
		this.httpError = '';
		if (this.form.invalid) return;

		if (this.loading) return;
		this.loading = true;

		this.getSaveObservable().subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				this.loading = false;
			},
		});
	}

	private getSaveObservable() {
		// trim whitespace
		this.zipCode.from = this.zipCode.from.trim();
		this.zipCode.to = this.zipCode.to?.trim();

		// Transfer from from if to is not set
		this.zipCode.to ??= this.zipCode.from;

		if ('self' in this.zipCode) {
			return this.palletisingService.updateZipCode(this.zipCode);
		}
		return this.palletisingService.createZipCode(this.zipCode);
	}
}
